import React, { useState } from 'react';
import { Dropdown, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import './commercial.css';
import { mediaTariffScopeV1018 } from '../../components/mediaTariffV1018';

export function PriceOfferSelectorV106({ offers = [], selected, onChange }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const [open, setOpen] = useState(false);
  if (!offers.length) return null;
  const active = offers.find((offer) => offer.id === selected) || offers[0];
  return (
    <Dropdown
      className='commercial-offer-select-v106'
      text={`${active.name} x${active.effective_multiplier}`}
      aria-label={zh ? '价格档位' : 'Price tier'}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <Dropdown.Menu>
        {offers.map((offer) => (
          <Dropdown.Item
            key={offer.id}
            active={active.id === offer.id}
            text={`${offer.name} x${offer.effective_multiplier}`}
            onClick={() => {
              onChange?.(offer.id);
              setOpen(false);
            }}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export const sourceQuoteLabelV107 = (quote) => {
  const name = String(quote?.name || quote?.id || '').trim();
  return name;
};

export const selectAvailableSourceQuotesV107 = (quotes = []) => {
  const items = Array.isArray(quotes) ? quotes : [];
  const containerIds = new Set(
    items
      .filter((quote) => quote?.kind === 'openlux')
      .map((quote) => quote.id)
      .filter(Boolean)
  );
  return items
    .filter(
      (quote) =>
        quote &&
        quote.pricing_mode === 'reference' &&
        quote.billable === false &&
        quote.kind !== 'openlux' &&
        !containerIds.has(quote.id) &&
        Array.isArray(quote.customer_prices) &&
        quote.customer_prices.length > 0
    )
    .sort((left, right) => {
      const leftMultiplier = Number(left.effective_multiplier);
      const rightMultiplier = Number(right.effective_multiplier);
      const leftFinite = Number.isFinite(leftMultiplier);
      const rightFinite = Number.isFinite(rightMultiplier);
      if (leftFinite && rightFinite && leftMultiplier !== rightMultiplier) {
        return leftMultiplier - rightMultiplier;
      }
      if (leftFinite !== rightFinite) return leftFinite ? -1 : 1;
      return sourceQuoteLabelV107(left).localeCompare(
        sourceQuoteLabelV107(right)
      );
    });
};

export function SourcePriceSelectorV107({ quotes = [], selected, onChange, formatMultiplier = (value) => value }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const [open, setOpen] = useState(false);
  const options = selectAvailableSourceQuotesV107(quotes);
  if (!options.length) return null;
  const active = options.find((quote) => quote.id === selected) || options[0];
  const multiplier = (quote) => formatMultiplier(quote.multiplier || quote.effective_multiplier);
  const sourceName = active.name || active.id;
  const tooltip = (
    <dl className='commercial-source-tooltip-v108'>
      <div><dt>{zh ? '渠道 source' : 'Channel source'}</dt><dd>{sourceName}</dd></div>
      <div><dt>{zh ? '倍率' : 'Multiplier'}</dt><dd>{active.multiplier ? `${formatMultiplier(active.multiplier)}x` : (zh ? '未提供' : 'Not provided')}</dd></div>
      {active.effective_multiplier && active.effective_multiplier !== active.multiplier &&
        <div><dt>{zh ? '客户参考倍率' : 'Customer reference multiplier'}</dt><dd>{formatMultiplier(active.effective_multiplier)}x</dd></div>}
    </dl>
  );
  return (
    <Popup content={tooltip} on={['hover', 'focus']} position='top center' trigger={
    <Dropdown
      className='commercial-offer-select-v106 commercial-source-select-v107'
      trigger={
        <span className='text model-source-choice-v108'>
          <span className='model-source-name-v108'>{sourceQuoteLabelV107(active)}</span>
          {' '}
          <span className='model-source-factor-v108'>x{multiplier(active)}</span>
        </span>
      }
      aria-label={zh ? '渠道 source 倍率' : 'Channel source multiplier'}
      value={active.id}
      openOnFocus={false}
      selectOnBlur={false}
      selectOnNavigation={false}
      options={options.map((quote) => ({
        key: quote.id,
        value: quote.id,
        text: `${sourceQuoteLabelV107(quote)} x${multiplier(quote)}`,
      }))}
      onChange={(_, { value }) => {
        onChange?.(value);
        setOpen(false);
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    />
    } />
  );
}

export function CommercialPriceVectorV106({ offer }) {
  const { t, i18n } = useTranslation();
  if (!offer) return null;
  return (
    <>
    {offer.price_is_estimate && <small>{i18n?.language?.startsWith('zh') ? '当前月度档位参考价' : 'Current monthly-tier estimate'}</small>}
    <dl className='commercial-vector-v106'>
      {(offer.customer_prices || []).map((price, index) => (
        <div key={`${price.meter}:${JSON.stringify(price.attributes)}:${index}`}>
          <dt>
            {t(`models.commercial.meters.${price.meter}`, { defaultValue: price.meter })}
            {price.attributes?.resolution ? ` ${String(price.attributes.resolution).toUpperCase()}` : ''}
            {price.meter === 'media_tariff' ? <small>{mediaTariffScopeV1018(price.attributes)}</small> : Object.entries(price.attributes || {}).filter(([key]) => key !== 'resolution').map(([key, value]) => (
              <small key={key}> {t(`models.commercial.attributes.${key}`, { defaultValue: key })}: {String(value)}</small>
            ))}
          </dt>
          <dd>
            ${price.unit_amount_usd}
            <span> / {t(`models.commercial.units.${price.unit}`, { defaultValue: price.unit })}</span>
          </dd>
        </div>
      ))}
    </dl>
    </>
  );
}
